import { Visibility } from '@mui/icons-material';
import {
  Alert,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Typography
} from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../../forms';
import { Dispatch, useCallback, useRef, useState } from 'react';
import * as Yup from 'yup';
import { VTextField, VTextFieldCPF, VTextFieldPhone } from '../../../../forms';
import getValidationErrors from '../../../../helpers/getValidationErrors';
import { ICreateUser } from '../../../../services';
import { IFormData } from '../LoginPage';

const createSchema: Yup.Schema<IFormData> = Yup.object().shape({
  name: Yup.string().required().min(5),
  email: Yup.string().email().required(),
  password: Yup.string().required().min(5),
  passwordConfirmation: Yup.string()
    .required()
    .min(5)
    .oneOf([Yup.ref('password')], 'A confirmação de senha deve ser igual a senha'),
});

interface IProps {
  errorMessage: string | undefined;
  showPassword: boolean | undefined;
  setShowPassword: Dispatch<React.SetStateAction<boolean>>;
  mutateCreate: UseMutateFunction<string | void, any, ICreateUser, unknown>;
  isLoadingCreate: boolean;
  initialData?: Partial<IFormData>;
}

export const Register: React.FC<IProps> = ({
  errorMessage,
  showPassword,
  setShowPassword,
  mutateCreate,
  isLoadingCreate,
  initialData,
}) => {
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const formRef = useRef<FormHandles>(null);

  const handleSubmit = useCallback(async (data: IFormData) => {
    try {
      formRef.current?.setErrors({});

      await createSchema.validate(data, { abortEarly: false });

      mutateCreate({
        name: data.name,
        email: data.email!,
        cpf: data.cpf,
        phone: data.phone,
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
      });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err as Yup.ValidationError);
        formRef.current?.setErrors(errors);
      }
    }
  }, []);

  return (
    <Form ref={formRef} onSubmit={handleSubmit} id='register-form' initialData={initialData}>
      <Box display='flex' flexDirection='column' gap={2}>
        <Typography variant='h5' align='center'>
          Faça o seu cadastro
        </Typography>

        <Divider variant='middle' />

        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

        <VTextField
          fullWidth
          label='Nome completo'
          name='name'
          required
          disabled={isLoadingCreate}
        />

        <Box display='flex' gap={2}>
          <VTextFieldCPF name='cpf' label='CPF' disabled={isLoadingCreate} />

          <VTextFieldPhone
            name='phone'
            label='Telefone'
            disabled={isLoadingCreate}
          />
        </Box>

        <VTextField
          fullWidth
          label='E-mail'
          name='email'
          type='email'
          required
          disabled={isLoadingCreate}
        />

        <Box display='flex' gap={2}>
          <VTextField
            fullWidth
            label='Senha'
            name='password'
            type={showPassword ? 'text' : 'password'}
            required
            disabled={isLoadingCreate}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Visibility />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <VTextField
            fullWidth
            label='Confirme sua senha'
            name='passwordConfirmation'
            type={showPasswordConfirmation ? 'text' : 'password'}
            required
            disabled={isLoadingCreate}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    tabIndex={-1}
                    onClick={() =>
                      setShowPasswordConfirmation(!showPasswordConfirmation)
                    }
                  >
                    <Visibility />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Form>
  );
};
